import React, {useState, useMemo, useEffect, useRef, useCallback} from 'react';
import {Box, Typography, Tabs, Tab} from '@mui/material';
import {t} from 'i18next';
import {ISelectItem} from '../../../shared/types';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import {ReactComponent as ArrowForwardIcon} from '../../../assets/images/arrow-forward.svg';
import {ReactComponent as DeleteIcon} from '../../../assets/images/delete.svg';
import MenuIcon from '@mui/icons-material/Menu';
import ArrTreeView from '../arr-tree-view/ArrTreeView';
import './ArrTreeItemSelector.scss';

export interface TabGroup {
  groupTitle: string;
  treeData: TreeViewBaseItem<ExtendedTreeItemProps>[];
  preSelectedItems?: ISelectItem[];
  preselectedItemIdField?: string;
  treeItemIdField?: string;
  prefix?: string;
  treeId?: string;
  postfixFieldName?: string;
  showFileType?: boolean;
}

export interface TabItem {
  tabTitle: string;
  groups: TabGroup[];
}

export interface GroupedItemsGroup {
  groupIndex: number;
  groupTitle: string;
  treeId?: string;
  items: TreeViewBaseItem<ExtendedTreeItemProps>[];
}

export interface GroupedItemsByTab {
  tabIndex: number;
  tabTitle: string;
  groups: GroupedItemsGroup[];
}

export interface SelectedItemsData {
  items: TreeViewBaseItem<ExtendedTreeItemProps>[];
  groupedByTab?: GroupedItemsByTab[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => {
  return (
    <Box
      className="tab-panel"
      role="tabpanel"
      hidden={value !== index}
      id={`arr-tree-selector-tabpanel-${index}`}
      aria-labelledby={`arr-tree-selector-tab-${index}`}
      sx={{
        display: value === index ? 'flex' : 'none',
      }}
      {...other}
    >
      {value === index && <Box className="tab-panel-content">{children}</Box>}
    </Box>
  );
};

interface ArrTreeItemSelectorProps {
  tabs: TabItem[];
  availablePanelTitle?: string;
  selectedPanelTitle?: string;
  noItemsAvailableText?: string;
  noFieldsSelectedText?: string;
  transferButtonAriaLabel?: string;
  deleteButtonAriaLabel?: string;
  onSelectedItemsChange?: (data: SelectedItemsData) => void;
  enableGroupedByTab?: boolean;
  isPreExpanded?: boolean;
  disableSelectedInAvailable?: boolean;
  parentDisabled?: boolean;
  disabled?: boolean;
  showPanelTitle?: boolean;
}

const ArrTreeItemSelector: React.FC<ArrTreeItemSelectorProps> = ({
  tabs,
  availablePanelTitle = t('common.treeItemTransfer.availablePanelTitle'),
  selectedPanelTitle = t('common.treeItemTransfer.selectedPanelTitle'),
  noItemsAvailableText = t('common.treeItemTransfer.noItemsAvailableText'),
  noFieldsSelectedText = t('common.treeItemTransfer.noFieldsSelectedText'),
  transferButtonAriaLabel = t(
    'common.treeItemTransfer.transferButtonAriaLabel'
  ),
  deleteButtonAriaLabel = t('common.treeItemTransfer.deleteButtonAriaLabel'),
  onSelectedItemsChange,
  isPreExpanded = true,
  disableSelectedInAvailable = true,
  parentDisabled = true,
  disabled = false,
  showPanelTitle = true,
  enableGroupedByTab = true,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const activeTabGroups = useMemo(() => {
    if (!tabs || tabs.length === 0) return [];
    return tabs[activeTab]?.groups || [];
  }, [tabs, activeTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const activeTabPreselectedItems = useMemo(() => {
    const allPreselected: ISelectItem[] = [];
    activeTabGroups.forEach((group) => {
      if (group.preSelectedItems && group.preSelectedItems.length > 0) {
        allPreselected.push(...group.preSelectedItems);
      }
    });
    return allPreselected;
  }, [activeTabGroups]);

  const getPreselectedItemIdField = useCallback((group: TabGroup): string => {
    return group.preselectedItemIdField || 'id';
  }, []);

  const getTreeItemIdFieldForGroup = useCallback((group: TabGroup): string => {
    return group.treeItemIdField || 'id';
  }, []);

  const preselectedItemIds = useMemo(() => {
    if (!activeTabPreselectedItems || activeTabPreselectedItems.length === 0) {
      return [];
    }
    const allIds: string[] = [];
    activeTabGroups.forEach((group) => {
      if (group.preSelectedItems && group.preSelectedItems.length > 0) {
        const groupField = getPreselectedItemIdField(group);
        group.preSelectedItems.forEach((item) => {
          const fieldValue = item[groupField];
          const id =
            typeof fieldValue === 'string'
              ? fieldValue
              : String(fieldValue ?? '');
          allIds.push(id);
        });
      }
    });
    return allIds;
  }, [activeTabPreselectedItems, activeTabGroups, getPreselectedItemIdField]);

  const getTreeItemId = useCallback(
    (
      item: TreeViewBaseItem<ExtendedTreeItemProps>,
      group?: TabGroup
    ): string => {
      const fieldToUse = group ? getTreeItemIdFieldForGroup(group) : 'id';
      const itemId = (item as Record<string, unknown>)[fieldToUse];
      return typeof itemId === 'string' ? itemId : String(itemId ?? '');
    },
    [getTreeItemIdFieldForGroup]
  );

  const findItemInNodes = useCallback(
    (
      treeId: string,
      itemId: string,
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      group?: TabGroup
    ): boolean => {
      for (const node of nodes) {
        const currentNodeId = getTreeItemId(node, group);
        if (currentNodeId === itemId && treeId == node.treeId) {
          return true;
        }
        if (node.children && node.children.length > 0) {
          if (findItemInNodes(treeId, itemId, node.children, group)) {
            return true;
          }
        }
      }
      return false;
    },
    [getTreeItemId]
  );

  // Maps to resolve item -> group by reference, and group -> key
  const nodeToGroupMapRef = useRef<
    WeakMap<TreeViewBaseItem<ExtendedTreeItemProps>, TabGroup>
  >(new WeakMap());
  const groupToKeyMapRef = useRef<Map<TabGroup, string>>(new Map());

  // Build reference maps whenever tabs change
  useEffect(() => {
    const nodeToGroup = new WeakMap<
      TreeViewBaseItem<ExtendedTreeItemProps>,
      TabGroup
    >();
    const groupToKey = new Map<TabGroup, string>();

    const assignGroup = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      group: TabGroup
    ) => {
      nodes.forEach((n) => {
        nodeToGroup.set(n, group);
        if (n.children && n.children.length > 0) {
          assignGroup(n.children, group);
        }
      });
    };

    tabs?.forEach((tab, tabIndex) => {
      tab.groups?.forEach((group, groupIndex) => {
        const key = `${tabIndex}-${groupIndex}`;
        groupToKey.set(group, key);
        assignGroup(group.treeData || [], group);
      });
    });

    nodeToGroupMapRef.current = nodeToGroup;
    groupToKeyMapRef.current = groupToKey;
  }, [tabs]);

  const findItemGroup = useCallback(
    (item: TreeViewBaseItem<ExtendedTreeItemProps>): TabGroup | undefined => {
      const byRef = nodeToGroupMapRef.current.get(item);
      if (byRef) return byRef;
      for (const tab of tabs) {
        for (const group of tab.groups || []) {
          const itemId = getTreeItemId(item, group);
          const found = findItemInNodes(
            item.treeId || '',
            itemId,
            group.treeData,
            group
          );
          if (found) {
            return group;
          }
        }
      }
      return undefined;
    },
    [tabs, getTreeItemId, findItemInNodes]
  );

  const groupItemsByTab = useCallback(
    (items: TreeViewBaseItem<ExtendedTreeItemProps>[]): GroupedItemsByTab[] => {
      if (!enableGroupedByTab) {
        return [];
      }
      const grouped: GroupedItemsByTab[] = [];

      tabs?.forEach((tab: TabItem, tabIndex: number) => {
        const tabGroups: GroupedItemsGroup[] = [];

        tab.groups?.forEach((group: TabGroup, groupIndex: number) => {
          const groupItems: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
          items.forEach((item) => {
            const itemId = getTreeItemId(item, group);
            if (
              findItemInNodes(item.treeId || '', itemId, group.treeData, group)
            ) {
              groupItems.push(item);
            }
          });
          if (groupItems.length > 0) {
            tabGroups.push({
              groupIndex,
              groupTitle: group.groupTitle,
              treeId: group.treeId,
              items: groupItems,
            });
          }
        });

        if (tabGroups.length > 0) {
          grouped.push({
            tabIndex,
            tabTitle: tab.tabTitle,
            groups: tabGroups,
          });
        }
      });

      return grouped;
    },
    [tabs, getTreeItemId, findItemInNodes]
  );

  const [selectedItems, setSelectedItems] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);

  const [removedPreselectedIds, setRemovedPreselectedIds] = useState<
    Set<string>
  >(new Set());

  // Helper function to generate unique group key
  const getGroupKey = useCallback(
    (tabIndex: number, groupIndex: number): string => {
      return `${tabIndex}-${groupIndex}`;
    },
    []
  );

  // State to track selections per group: Map<groupKey, string[]>
  const [treeSelectedItemsByGroup, setTreeSelectedItemsByGroup] = useState<
    Map<string, string[]>
  >(new Map());

  // State to track checked fields per group: Map<groupKey, Set<string>>
  const [checkedFieldsByGroup, setCheckedFieldsByGroup] = useState<
    Map<string, Set<string>>
  >(new Map());

  const [_activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const getLeafNodes = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    const leafNodes: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
    const traverse = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      nodes.forEach((node) => {
        if (!node.children || node.children.length === 0) {
          leafNodes.push(node);
        } else {
          traverse(node.children);
        }
      });
    };
    traverse(nodes);
    return leafNodes;
  };

  const availableLeafNodes = useMemo(() => {
    const allLeafNodes: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
    activeTabGroups.forEach((group) => {
      allLeafNodes.push(...getLeafNodes(group.treeData));
    });
    return allLeafNodes;
  }, [activeTabGroups]);

  const selectedCompositeKeySet = useMemo(() => {
    const keys = new Set<string>();
    selectedItems.forEach((item) => {
      const group = findItemGroup(item);
      if (group) {
        const groupKey = groupToKeyMapRef.current.get(group) || '';
        const id = getTreeItemId(item, group);
        keys.add(`${groupKey}|${id}`);
      } else {
        // Fallback: try to find group by searching through tabs
        // This handles cases where findItemGroup fails (e.g., for preselected items)
        for (const tab of tabs) {
          for (const group of tab.groups || []) {
            const itemId = getTreeItemId(item, group);
            if (
              findItemInNodes(item.treeId || '', itemId, group.treeData, group)
            ) {
              const groupKey = groupToKeyMapRef.current.get(group) || '';
              keys.add(`${groupKey}|${itemId}`);
              break;
            }
          }
        }
      }
    });
    return keys;
  }, [selectedItems, getTreeItemId, findItemGroup, tabs, findItemInNodes]);

  const markDisabledLeaves = useCallback(
    (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      group?: TabGroup
    ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
      // If component is disabled, mark all nodes as disabled
      if (disabled) {
        return nodes.map((node) => {
          const processedChildren = node.children
            ? markDisabledLeaves(node.children, group)
            : undefined;
          return {
            ...node,
            disabled: true,
            children: processedChildren,
          };
        });
      }

      if (!disableSelectedInAvailable) {
        return nodes;
      }
      return nodes.map((node) => {
        const nodeId = getTreeItemId(node, group);
        const isLeaf = !node.children || node.children.length === 0;

        const groupKeyForGroup = group
          ? groupToKeyMapRef.current.get(group) || ''
          : '';
        const composite = `${groupKeyForGroup}|${nodeId}`;
        const isSelectedByComposite = selectedCompositeKeySet.has(composite);

        // Fallback: check if node is directly in selectedItems (by reference or ID match)
        // This handles cases where composite key matching fails (e.g., for preselected items)
        const isSelectedByReference = selectedItems.some((selectedItem) => {
          if (selectedItem === node) return true; // Same reference
          const selectedGroup = findItemGroup(selectedItem);
          if (!selectedGroup || !group) return false;
          // Check if same group and same ID
          if (selectedGroup.treeId !== group.treeId) return false;
          const selectedId = getTreeItemId(selectedItem, selectedGroup);
          return selectedId === nodeId;
        });

        const isSelected = isSelectedByComposite || isSelectedByReference;
        const shouldDisableLeaf = isLeaf && isSelected;

        let processedChildren:
          | TreeViewBaseItem<ExtendedTreeItemProps>[]
          | undefined;
        let shouldDisableParent = false;

        if (!isLeaf && node.children) {
          processedChildren = markDisabledLeaves(node.children, group);

          const allChildrenDisabled =
            processedChildren.length > 0 &&
            processedChildren.every((child) => child.disabled === true);

          // Disable parent if it's selected OR (if parentDisabled is true AND all children are disabled)
          shouldDisableParent =
            isSelected || (parentDisabled && allChildrenDisabled);
        }

        return {
          ...node,
          disabled: isLeaf
            ? (node.disabled ?? false) || shouldDisableLeaf
            : (node.disabled ?? false) || shouldDisableParent,
          children: processedChildren,
        };
      });
    },
    [
      selectedCompositeKeySet,
      selectedItems,
      getTreeItemId,
      findItemGroup,
      disableSelectedInAvailable,
      parentDisabled,
      disabled,
    ]
  );

  const initializedTabsRef = useRef<Set<number>>(new Set());
  const hasInitializedAllTabsRef = useRef(false);
  const previousPreselectedIdsRef = useRef<string>('');

  const getAllParentIds = useCallback(
    (
      nodeId: string,
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      parentPath: string[] = [],
      group?: TabGroup
    ): string[] => {
      for (const node of nodes) {
        const currentNodeId = getTreeItemId(node, group);
        const currentPath = [...parentPath, currentNodeId];

        if (currentNodeId === nodeId) {
          return parentPath;
        }

        if (node.children && node.children.length > 0) {
          const found = getAllParentIds(
            nodeId,
            node.children,
            currentPath,
            group
          );
          if (found.length > 0 || currentNodeId === nodeId) {
            return found.length > 0 ? found : parentPath;
          }
        }
      }
      return [];
    },
    [getTreeItemId]
  );

  // Track preselected items IDs to detect changes
  const currentPreselectedIds = useMemo(() => {
    const makeScopedId = (group: TabGroup | undefined, itemId: string) =>
      `${group?.treeId || 'unknownTree'}::${itemId}`;

    const allIds: string[] = [];
    tabs?.forEach((tab) => {
      tab.groups?.forEach((group) => {
        if (group.preSelectedItems?.length) {
          const field = getPreselectedItemIdField(group);
          group.preSelectedItems.forEach((item) => {
            const raw = item[field];
            const itemId = typeof raw === 'string' ? raw : String(raw ?? '');
            allIds.push(makeScopedId(group, itemId));
          });
        }
      });
    });
    return allIds.sort().join(',');
  }, [tabs, getPreselectedItemIdField]);

  // Detect when preSelectedItems change and reset initialization to allow re-initialization
  const isReinitializationRef = useRef(false);
  useEffect(() => {
    if (currentPreselectedIds !== previousPreselectedIdsRef.current) {
      // preSelectedItems changed - reset initialization flag to allow re-initialization
      // Only reset if not during user interaction
      if (!isUserInteractionRef.current) {
        const wasInitialized = previousPreselectedIdsRef.current !== '';
        hasInitializedAllTabsRef.current = false;
        isReinitializationRef.current = wasInitialized;
        // Clear removed preselected IDs since we're getting new preselected items
        setRemovedPreselectedIds(new Set());
      }
      previousPreselectedIdsRef.current = currentPreselectedIds;
    }
  }, [currentPreselectedIds]);

  useEffect(() => {
    if (!hasInitializedAllTabsRef.current && tabs && tabs.length > 0) {
      const makeScopedId = (group: TabGroup | undefined, itemId: string) =>
        `${group?.treeId || 'unknownTree'}::${itemId}`;

      const allPreselectedItemIds = new Set<string>();
      const allPreselectedItems: {
        item: TreeViewBaseItem<ExtendedTreeItemProps>;
        group: TabGroup;
      }[] = [];

      // STEP 1: Gather all preselected IDs
      tabs.forEach((tab) => {
        tab.groups?.forEach((group) => {
          if (group.preSelectedItems?.length) {
            const field = getPreselectedItemIdField(group);
            group.preSelectedItems.forEach((item) => {
              const raw = item[field];
              const itemId = typeof raw === 'string' ? raw : String(raw ?? '');
              allPreselectedItemIds.add(makeScopedId(group, itemId));
            });
          }
        });
      });

      // STEP 2: Collect matching nodes within their own group
      const findAllPreselectedNodes = (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
        group: TabGroup
      ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
        const found: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
        nodes.forEach((node) => {
          const nodeId = getTreeItemId(node, group);
          const scopedId = makeScopedId(group, nodeId);

          if (allPreselectedItemIds.has(scopedId)) {
            found.push(node);
          }

          if (node.children?.length) {
            found.push(...findAllPreselectedNodes(node.children, group));
          }
        });
        return found;
      };

      tabs.forEach((tab) => {
        tab.groups?.forEach((group) => {
          const foundNodes = findAllPreselectedNodes(group.treeData, group);
          foundNodes.forEach((node) => {
            const scopedId = makeScopedId(group, getTreeItemId(node, group));
            // Only add once per group
            if (
              !allPreselectedItems.some(
                (i) =>
                  makeScopedId(i.group, getTreeItemId(i.item, i.group)) ===
                  scopedId
              )
            ) {
              allPreselectedItems.push({item: node, group});
            }
          });
        });
      });

      // STEP 3: Update selected items
      if (allPreselectedItems.length > 0) {
        // Capture re-initialization flag before state update
        const isReinitializing = isReinitializationRef.current;

        setSelectedItems((prev) => {
          // If re-initializing (preSelectedItems changed), replace selectedItems with only the new preselected items
          // Otherwise, add new items to existing ones (initial load)
          if (isReinitializing) {
            // Replace all selectedItems with the new preselected items (ignore prev)
            const updatedItems = allPreselectedItems.map(({item}) => item);
            const groupedByTab = groupItemsByTab(updatedItems);
            onSelectedItemsChange?.({items: updatedItems, groupedByTab});
            return updatedItems;
          }

          // Initial load: add new items to existing ones
          const existingIds = new Set<string>();
          prev.forEach((item) => {
            const group = findItemGroup(item);
            if (group) {
              existingIds.add(makeScopedId(group, getTreeItemId(item, group)));
            }
          });

          const processedScopedIds = new Set<string>();
          const newItems: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

          allPreselectedItems.forEach(({item, group}) => {
            const nodeId = getTreeItemId(item, group);
            const scopedId = makeScopedId(group, nodeId);

            if (
              !existingIds.has(scopedId) &&
              !processedScopedIds.has(scopedId)
            ) {
              if (
                findItemInNodes(
                  group.treeId || '',
                  nodeId,
                  group.treeData,
                  group
                )
              ) {
                newItems.push(item);
                processedScopedIds.add(scopedId);
              }
            }
          });

          if (newItems.length > 0) {
            const updatedItems = [...prev, ...newItems];
            const groupedByTab = groupItemsByTab(updatedItems);
            onSelectedItemsChange?.({items: updatedItems, groupedByTab});
            return updatedItems;
          }

          return prev;
        });

        // Reset the flag after processing
        if (isReinitializing) {
          isReinitializationRef.current = false;
        }

        // STEP 4: Update group selection map
        setTreeSelectedItemsByGroup((prev) => {
          const updated = new Map(prev);

          tabs.forEach((tab, tabIndex) => {
            tab.groups?.forEach((group, groupIndex) => {
              const groupKey = getGroupKey(tabIndex, groupIndex);
              const selectedIds = new Set(updated.get(groupKey) || []);

              allPreselectedItems.forEach(({item, group: itemGroup}) => {
                // Only apply to matching group
                if (group.treeId !== itemGroup.treeId) return;

                const itemId = getTreeItemId(item, group);
                if (
                  findItemInNodes(
                    group.treeId || '',
                    itemId,
                    group.treeData,
                    group
                  )
                ) {
                  selectedIds.add(itemId);
                  const parentIds = getAllParentIds(
                    itemId,
                    group.treeData,
                    [],
                    group
                  );
                  parentIds.forEach((pid) => selectedIds.add(pid));
                }
              });

              if (selectedIds.size > 0) {
                updated.set(groupKey, Array.from(selectedIds));
              }
            });
          });

          return updated;
        });
      }

      hasInitializedAllTabsRef.current = true;
      // Reset re-initialization flag after initialization completes
      isReinitializationRef.current = false;
    }
  }, [
    tabs,
    getTreeItemId,
    getPreselectedItemIdField,
    findItemGroup,
    findItemInNodes,
    onSelectedItemsChange,
    getAllParentIds,
    groupItemsByTab,
    getGroupKey,
  ]);

  useEffect(() => {
    if (
      preselectedItemIds.length > 0 &&
      !initializedTabsRef.current.has(activeTab) &&
      availableLeafNodes.length > 0
    ) {
      initializedTabsRef.current.add(activeTab);

      setTreeSelectedItemsByGroup((prev) => {
        const updated = new Map(prev);
        activeTabGroups.forEach((group, groupIndex) => {
          const groupKey = getGroupKey(activeTab, groupIndex);
          const selectedIds = new Set(updated.get(groupKey) || []);

          preselectedItemIds.forEach((id) => {
            // Only add if this ID exists in this group's tree
            if (
              findItemInNodes(group.treeId || '', id, group.treeData, group)
            ) {
              selectedIds.add(id);
              const parentIds = getAllParentIds(id, group.treeData, [], group);
              parentIds.forEach((parentId) => selectedIds.add(parentId));
            }
          });

          if (selectedIds.size > 0) {
            updated.set(groupKey, Array.from(selectedIds));
          }
        });
        return updated;
      });
    }
  }, [
    preselectedItemIds,
    activeTab,
    availableLeafNodes,
    activeTabGroups,
    getAllParentIds,
    getGroupKey,
    findItemInNodes,
  ]);

  const isUserInteractionRef = useRef(false);
  const lastSyncedSelectedItemsRef = useRef<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const lastSyncedPreselectedIdsRef = useRef<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const lastTreeSelectionsSerializedRef = useRef<string>('');

  // Sync selectedItems with group-specific tree selections
  // Skip when user is interacting with tree to prevent infinite loops
  useEffect(() => {
    // Skip sync if user is currently interacting with the tree
    if (isUserInteractionRef.current) {
      return;
    }

    // Check if selectedItems or preselectedItemIds actually changed
    // Compare by reference first (fast path)
    const selectedItemsChanged =
      selectedItems !== lastSyncedSelectedItemsRef.current &&
      (selectedItems.length !== lastSyncedSelectedItemsRef.current.length ||
        selectedItems.some(
          (item, index) => item !== lastSyncedSelectedItemsRef.current[index]
        ));

    // Compare preselected IDs by value
    const preselectedIdsChanged =
      preselectedItemIds.length !==
        lastSyncedPreselectedIdsRef.current.length ||
      preselectedItemIds.some(
        (id) => !lastSyncedPreselectedIdsRef.current.includes(id)
      ) ||
      lastSyncedPreselectedIdsRef.current.some(
        (id) => !preselectedItemIds.includes(id)
      );

    // Only sync if something actually changed
    if (!selectedItemsChanged && !preselectedIdsChanged) {
      return;
    }

    // Use functional update to avoid dependency on prev state
    setTreeSelectedItemsByGroup((prev) => {
      // Skip if user interaction started during this update
      if (isUserInteractionRef.current) {
        return prev;
      }

      const updated = new Map(prev);
      let hasChanges = false;

      tabs.forEach((tab, tabIndex) => {
        tab.groups?.forEach((group, groupIndex) => {
          const groupKey = getGroupKey(tabIndex, groupIndex);
          const selectedIds = new Set<string>();

          // Add selected items that belong to this group
          selectedItems.forEach((item) => {
            const itemGroup = findItemGroup(item);
            if (itemGroup === group) {
              const itemId = getTreeItemId(item, group);
              selectedIds.add(itemId);
              const parentIds = getAllParentIds(
                itemId,
                group.treeData,
                [],
                group
              );
              parentIds.forEach((parentId) => selectedIds.add(parentId));
            }
          });

          // Add preselected items that belong to this group and haven't been removed
          // Only add if the actual node object belongs to this group (not just if ID exists)
          if (preselectedItemIds.length > 0) {
            preselectedItemIds.forEach((id) => {
              if (!removedPreselectedIds.has(id)) {
                // Find the actual node in selectedItems that has this ID
                const preselectedNode = selectedItems.find((item) => {
                  const itemGroup = findItemGroup(item);
                  if (!itemGroup) return false;
                  const itemId = getTreeItemId(item, itemGroup);
                  return itemId === id && itemGroup === group;
                });

                // Only add if we found the node and it belongs to this specific group
                if (preselectedNode) {
                  selectedIds.add(id);
                  const parentIds = getAllParentIds(
                    id,
                    group.treeData,
                    [],
                    group
                  );
                  parentIds.forEach((parentId) => selectedIds.add(parentId));
                }
              }
            });
          }

          const newSelection = Array.from(selectedIds).sort();
          const currentSelection = (prev.get(groupKey) || []).sort();

          // Only update if selection actually changed (compare sorted arrays)
          if (
            newSelection.length !== currentSelection.length ||
            newSelection.some((id, idx) => id !== currentSelection[idx])
          ) {
            hasChanges = true;
            if (newSelection.length > 0) {
              updated.set(groupKey, Array.from(selectedIds));
            } else {
              updated.delete(groupKey);
            }
          }
        });
      });

      // Only return new map if there are actual changes
      if (hasChanges) {
        // Update refs to track what we synced
        lastSyncedSelectedItemsRef.current = [...selectedItems];
        lastSyncedPreselectedIdsRef.current = [...preselectedItemIds];
        return updated;
      }
      return prev;
    });
  }, [selectedItems, preselectedItemIds, removedPreselectedIds, tabs]);

  // Update checked fields per group based on tree selections
  useEffect(() => {
    setCheckedFieldsByGroup((prev) => {
      const updated = new Map(prev);
      let hasChanges = false;

      activeTabGroups.forEach((group, groupIndex) => {
        const groupKey = getGroupKey(activeTab, groupIndex);
        const groupSelectedItems = treeSelectedItemsByGroup.get(groupKey) || [];
        const nodeIds = new Set(groupSelectedItems);
        const checkedIds = new Set<string>();

        // Only check leaf nodes that actually belong to this specific group
        const groupLeafNodes = getLeafNodes(group.treeData);
        groupLeafNodes.forEach((node) => {
          const nodeId = getTreeItemId(node, group);
          // Verify the node belongs to this group by checking the WeakMap
          const nodeGroup = nodeToGroupMapRef.current.get(node);
          // Only add if the ID is in this group's selections AND the node belongs to this group
          if (nodeIds.has(nodeId) && nodeGroup === group) {
            checkedIds.add(nodeId);
          }
        });

        const prevChecked = prev.get(groupKey) || new Set<string>();
        // Only update if checked fields actually changed
        if (
          checkedIds.size !== prevChecked.size ||
          !Array.from(checkedIds).every((id) => prevChecked.has(id))
        ) {
          hasChanges = true;
          updated.set(groupKey, checkedIds);
        }
      });

      return hasChanges ? updated : prev;
    });
  }, [
    treeSelectedItemsByGroup,
    activeTabGroups,
    activeTab,
    getGroupKey,
    getTreeItemId,
    getLeafNodes,
  ]);

  const handleTreeSelection = useCallback(
    (groupKey: string) => {
      return (selectedIds: string[] | null) => {
        if (disabled) return;
        isUserInteractionRef.current = true;
        setTreeSelectedItemsByGroup((prev) => {
          const updated = new Map(prev);
          if (selectedIds && selectedIds.length > 0) {
            updated.set(groupKey, selectedIds);
          } else {
            updated.delete(groupKey);
          }
          // Reset flag after update is queued
          setTimeout(() => {
            isUserInteractionRef.current = false;
          }, 100);
          return updated;
        });
      };
    },
    [disabled]
  );

  const handleTransferSelected = () => {
    if (disabled) return;
    const nodesToTransfer: Array<{
      node: TreeViewBaseItem<ExtendedTreeItemProps>;
      group: TabGroup;
      groupKey: string;
    }> = [];

    activeTabGroups.forEach((group, groupIndex) => {
      const groupKey = getGroupKey(activeTab, groupIndex);
      const groupCheckedFields =
        checkedFieldsByGroup.get(groupKey) || new Set<string>();
      const groupLeafNodes = getLeafNodes(group.treeData);

      groupLeafNodes.forEach((node) => {
        const nodeId = getTreeItemId(node, group);
        if (groupCheckedFields.has(nodeId)) {
          nodesToTransfer.push({node, group, groupKey});
        }
      });
    });

    if (nodesToTransfer.length > 0) {
      const newItems = nodesToTransfer.filter(({node, group, groupKey}) => {
        const nodeId = getTreeItemId(node, group);
        const nodeCompositeKey = `${groupKey}|${nodeId}`;
        return !selectedItems.some((si) => {
          const siGroup = findItemGroup(si);
          if (!siGroup) return false;
          const siGroupKey = groupToKeyMapRef.current.get(siGroup) || '';
          const siId = getTreeItemId(si, siGroup);
          const siCompositeKey = `${siGroupKey}|${siId}`;
          return siCompositeKey === nodeCompositeKey;
        });
      });

      if (newItems.length > 0) {
        const nodesToAdd = newItems.map(({node}) => node);
        const updatedItems = [...selectedItems, ...nodesToAdd];
        setSelectedItems(updatedItems);
        const groupedByTab = groupItemsByTab(updatedItems);
        onSelectedItemsChange?.({items: updatedItems, groupedByTab});

        // Update tree selections per group
        setTreeSelectedItemsByGroup((prev) => {
          const updated = new Map(prev);
          newItems.forEach(({node, group, groupKey}) => {
            const itemId = getTreeItemId(node, group);
            const currentSelected = new Set(updated.get(groupKey) || []);
            currentSelected.add(itemId);
            const parentIds = getAllParentIds(
              itemId,
              group.treeData,
              [],
              group
            );
            parentIds.forEach((parentId) => currentSelected.add(parentId));
            updated.set(groupKey, Array.from(currentSelected));
          });
          return updated;
        });

        // Clear checked fields for all active groups
        setCheckedFieldsByGroup((prev) => {
          const updated = new Map(prev);
          activeTabGroups.forEach((_, groupIndex) => {
            const groupKey = getGroupKey(activeTab, groupIndex);
            updated.set(groupKey, new Set<string>());
          });
          return updated;
        });
      }
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (over && active.id !== over.id) {
      const activeId = String(active.id);
      const overId = String(over.id);
      const toComposite = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
        const group = findItemGroup(node);
        if (!group) return '';
        const groupKey = groupToKeyMapRef.current.get(group) || '';
        const id = getTreeItemId(node, group);
        return `${groupKey}|${id}`;
      };
      const oldIndex = selectedItems.findIndex(
        (node) => toComposite(node) === activeId
      );
      const newIndex = selectedItems.findIndex(
        (node) => toComposite(node) === overId
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedItems = arrayMove(selectedItems, oldIndex, newIndex);
        setSelectedItems(reorderedItems);
        const groupedByTab = groupItemsByTab(reorderedItems);
        onSelectedItemsChange?.({items: reorderedItems, groupedByTab});
      }
    }

    setActiveId(null);
  };

  const handleDeleteItem = (compositeId: string) => {
    if (disabled) return;
    const [targetGroupKey, targetItemId] = compositeId.split('|');
    const deletedItem = selectedItems.find((node) => {
      const group = findItemGroup(node);
      if (!group) return false;
      const groupKey = groupToKeyMapRef.current.get(group) || '';
      return (
        groupKey === targetGroupKey &&
        getTreeItemId(node, group) === targetItemId
      );
    });

    const deletedItemGroup = deletedItem
      ? findItemGroup(deletedItem)
      : undefined;

    const updatedItems = selectedItems.filter((node) => {
      const group = findItemGroup(node);
      if (!group) return true;
      const groupKey = groupToKeyMapRef.current.get(group) || '';
      return !(
        groupKey === targetGroupKey &&
        getTreeItemId(node, group) === targetItemId
      );
    });

    isUserInteractionRef.current = true;

    const allPreselectedIds = new Set<string>();
    tabs?.forEach((tab) => {
      tab.groups?.forEach((group) => {
        if (group.preSelectedItems && group.preSelectedItems.length > 0) {
          const groupField = getPreselectedItemIdField(group);
          group.preSelectedItems.forEach((item) => {
            const fieldValue = item[groupField];
            const id =
              typeof fieldValue === 'string'
                ? fieldValue
                : String(fieldValue ?? '');
            allPreselectedIds.add(id);
          });
        }
      });
    });
    if (allPreselectedIds.has(targetItemId)) {
      setRemovedPreselectedIds((prev) => {
        const updated = new Set(prev);
        updated.add(targetItemId);
        return updated;
      });
    }

    setSelectedItems(updatedItems);
    const groupedByTab = groupItemsByTab(updatedItems);
    onSelectedItemsChange?.({items: updatedItems, groupedByTab});

    // Update tree selections for the group that contained the deleted item
    if (deletedItemGroup) {
      tabs.forEach((tab, tabIndex) => {
        tab.groups?.forEach((group, groupIndex) => {
          if (group === deletedItemGroup) {
            const groupKey = getGroupKey(tabIndex, groupIndex);
            setTreeSelectedItemsByGroup((prev) => {
              const updated = new Map(prev);
              const currentSelected = new Set(updated.get(groupKey) || []);
              currentSelected.delete(targetItemId);

              // Remove parent IDs
              const parentIds = getAllParentIds(
                targetItemId,
                group.treeData,
                [],
                group
              );
              parentIds.forEach((parentId) => currentSelected.delete(parentId));

              // Add back parent IDs for remaining items in this group
              updatedItems.forEach((item) => {
                const itemGroup = findItemGroup(item);
                if (itemGroup === group) {
                  const remainingItemId = getTreeItemId(item, itemGroup);
                  const remainingParentIds = getAllParentIds(
                    remainingItemId,
                    group.treeData,
                    [],
                    group
                  );
                  remainingParentIds.forEach((parentId) =>
                    currentSelected.add(parentId)
                  );
                }
              });

              const updatedSelected = Array.from(currentSelected);
              if (updatedSelected.length > 0) {
                updated.set(groupKey, updatedSelected);
              } else {
                updated.delete(groupKey);
              }
              return updated;
            });
          }
        });
      });
    }
  };

  const SortableSelectedItem: React.FC<{
    item: TreeViewBaseItem<ExtendedTreeItemProps>;
  }> = ({item}) => {
    const group = findItemGroup(item);
    const itemId = group ? getTreeItemId(item, group) : '';
    const compositeId = group
      ? `${groupToKeyMapRef.current.get(group) || ''}|${itemId}`
      : itemId;
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: compositeId,
      disabled: disabled,
    });

    const selectedFieldItemSx = {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    let displayLabel = group?.prefix
      ? `${group.prefix} ${item.label || ''}`
      : item.label || '';

    // Get the field value to display in parentheses
    if (group?.postfixFieldName) {
      const fieldVal = (item as Record<string, unknown>)[
        group.postfixFieldName
      ];
      if (
        fieldVal !== undefined &&
        fieldVal !== null &&
        String(fieldVal).trim()
      ) {
        const fieldValue = String(fieldVal).trim();
        displayLabel = fieldValue
          ? `${displayLabel} (${fieldValue})`
          : displayLabel;
      }
    }

    return (
      <Box
        ref={setNodeRef}
        sx={selectedFieldItemSx}
        className={`selected-field-item ${isDragging ? 'dragging' : ''}`}
      >
        {!disabled && (
          <Box className="field-drag-handle" {...attributes} {...listeners}>
            <MenuIcon className="drag-icon" />
          </Box>
        )}
        <Typography
          className="typo-text body3_size field-label"
          title={displayLabel}
          data-testid={`arr-tree-item-selector-diplsay-label`}
        >
          {displayLabel}
        </Typography>
        {!disabled && (
          <ArrIconButton
            icon={<DeleteIcon />}
            onClick={() => handleDeleteItem(compositeId)}
            ariaLabel={deleteButtonAriaLabel}
            className="delete-button"
            testId={`delete-button-${compositeId}`}
          />
        )}
      </Box>
    );
  };

  return (
    <Box
      className={`arr-tree-item-selector ${disabled && 'disabled'} active-tab-${activeTab}`}
    >
      <Box className="fields-panel">
        {showPanelTitle && (
          <Box className="fields-bar">
            <Typography
              className="typo-text subhead4_size fields-title"
              data-testid={`arr-tree-item-selector-available-panel-title`}
            >
              {availablePanelTitle}
            </Typography>
          </Box>
        )}
        {tabs && tabs.length > 1 && (
          <Box className="tabs-container">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              className="fields-tabs"
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.tabTitle}
                  id={`arr-tree-selector-tab-${index}`}
                  aria-controls={`arr-tree-selector-tabpanel-${index}`}
                  className="typo-text subhead5_size"
                />
              ))}
            </Tabs>
          </Box>
        )}
        <Box className="fields-content">
          {tabs && tabs.length > 1 ? (
            tabs.map((tab, tabIndex) => {
              const hasAnyGroupData = tab.groups?.some(
                (group) => group.treeData && group.treeData.length > 0
              );
              const shouldShowGroupTitles = (tab.groups?.length || 0) > 1;

              return (
                <TabPanel key={tabIndex} value={activeTab} index={tabIndex}>
                  {!hasAnyGroupData ? (
                    <Typography
                      className="typo-text body3_size empty-state"
                      data-testid={`arr-tree-item-selector-typography-btn-${tabIndex}`}
                    >
                      {noItemsAvailableText}
                    </Typography>
                  ) : (
                    <Box className="groups-container">
                      {tab.groups?.map((group, groupIndex) => {
                        const groupTreeDataWithDisabled = markDisabledLeaves(
                          group.treeData,
                          group
                        );
                        const getGroupExpandedIds = (
                          nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
                        ): string[] => {
                          const ids: string[] = [];
                          nodes.forEach((node) => {
                            ids.push(getTreeItemId(node, group));
                            if (node.children && node.children.length > 0) {
                              ids.push(...getGroupExpandedIds(node.children));
                            }
                          });
                          return ids;
                        };
                        const groupDefaultExpandedIds = isPreExpanded
                          ? getGroupExpandedIds(group.treeData)
                          : [];

                        if (!group.treeData || group.treeData.length === 0) {
                          return null;
                        }

                        return (
                          <Box key={groupIndex} className="group-container">
                            {shouldShowGroupTitles && (
                              <Typography
                                className="typo-text body3_size group-title"
                                data-testid={`arr-tree-item-selector-typography-button-${groupIndex}`}
                              >
                                {group.groupTitle}
                              </Typography>
                            )}
                            <Box className="tree-container" sx={{flex: 1}}>
                              <ArrTreeView
                                itemsList={groupTreeDataWithDisabled}
                                treeSelectedItems={
                                  treeSelectedItemsByGroup.get(
                                    getGroupKey(tabIndex, groupIndex)
                                  ) || null
                                }
                                setSelectedItem={handleTreeSelection(
                                  getGroupKey(tabIndex, groupIndex)
                                )}
                                multipleSelection={true}
                                isShowCheckbox={true}
                                defaultExpandedIds={groupDefaultExpandedIds}
                                handleNoResultMessage={noItemsAvailableText}
                                isShowFileType={group.showFileType ?? false}
                                includeDisabledInSelectionState={true}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </TabPanel>
              );
            })
          ) : (
            <>
              {activeTabGroups.length === 0 ||
              !activeTabGroups.some(
                (g) => g.treeData && g.treeData.length > 0
              ) ? (
                <Typography
                  className="typo-text body3_size empty-state"
                  data-testid={`arr-tree-item-selector-typography-icon`}
                >
                  {noItemsAvailableText}
                </Typography>
              ) : (
                activeTabGroups.map((group, groupIndex) => (
                  <Box key={groupIndex} className="tree-container">
                    <ArrTreeView
                      itemsList={markDisabledLeaves(group.treeData, group)}
                      treeSelectedItems={
                        treeSelectedItemsByGroup.get(
                          getGroupKey(activeTab, groupIndex)
                        ) || null
                      }
                      setSelectedItem={handleTreeSelection(
                        getGroupKey(activeTab, groupIndex)
                      )}
                      multipleSelection={true}
                      isShowCheckbox={true}
                      defaultExpandedIds={
                        isPreExpanded
                          ? (() => {
                              const getAllIds = (
                                nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
                              ): string[] => {
                                const ids: string[] = [];
                                nodes.forEach((node) => {
                                  ids.push(getTreeItemId(node, group));
                                  if (
                                    node.children &&
                                    node.children.length > 0
                                  ) {
                                    ids.push(...getAllIds(node.children));
                                  }
                                });
                                return ids;
                              };
                              return getAllIds(group.treeData);
                            })()
                          : []
                      }
                      handleNoResultMessage={noItemsAvailableText}
                      isShowFileType={group.showFileType ?? false}
                      includeDisabledInSelectionState={true}
                    />
                  </Box>
                ))
              )}
            </>
          )}
        </Box>
      </Box>
      <Box className="transfer-button-container">
        <ArrIconButton
          testId="transfer-button"
          icon={<ArrowForwardIcon />}
          onClick={handleTransferSelected}
          ariaLabel={transferButtonAriaLabel}
          disabled={
            disabled ||
            Array.from(checkedFieldsByGroup.values()).every(
              (checkedSet) => checkedSet.size === 0
            )
          }
          className="transfer-button"
        />
      </Box>
      <Box className="fields-panel">
        <Box className="fields-bar">
          <Typography
            className="typo-text subhead4_size fields-title"
            data-testid={`arr-tree-item-selector-selected-panel-title`}
          >
            {selectedPanelTitle}
          </Typography>
        </Box>
        <Box className="fields-content">
          {selectedItems.length === 0 ? (
            <Typography
              className="typo-text body3_size empty-state"
              data-testid={`arr-tree-item-selector-no-field-selected`}
            >
              {noFieldsSelectedText}
            </Typography>
          ) : disabled ? (
            <Box>
              {selectedItems.map((item) => {
                const group = findItemGroup(item);
                const id = group ? getTreeItemId(item, group) : '';
                const groupKey = group
                  ? groupToKeyMapRef.current.get(group) || ''
                  : '';
                const compositeId = group ? `${groupKey}|${id}` : id;
                return <SortableSelectedItem key={compositeId} item={item} />;
              })}
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedItems.map((item) => {
                  const group = findItemGroup(item);
                  if (!group) return '';
                  const id = getTreeItemId(item, group);
                  const groupKey = groupToKeyMapRef.current.get(group) || '';
                  return `${groupKey}|${id}`;
                })}
                strategy={verticalListSortingStrategy}
              >
                {selectedItems.map((item) => {
                  const group = findItemGroup(item);
                  const id = group ? getTreeItemId(item, group) : '';
                  const groupKey = group
                    ? groupToKeyMapRef.current.get(group) || ''
                    : '';
                  const compositeId = group ? `${groupKey}|${id}` : id;
                  return <SortableSelectedItem key={compositeId} item={item} />;
                })}
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ArrTreeItemSelector;
